import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';
import type { PackageOffer, Guide } from '../../types';
import { formatSessionTimeInSeekerTimezone, getBrowserTimezone, getTimezoneAbbreviation, isToday, isTomorrow, formatTimeInTimezone, formatShortDateInTimezone } from '../../utils/dateUtils';
import { seekerDataService } from '../../services/seekerDataService';
import { AddToCalendarButton } from '../AddToCalendarButton';

interface ScheduledSession {
  id: string;
  guide_id: string;
  session_type: string;
  session_date: string;
  session_time: string;
  session_status: string;
  duration_minutes: number;
  location?: 'online' | 'in_person' | 'link' | null;
  location_details?: string | null;
  session_link?: string | null;
  scheduled_by?: 'seeker' | 'guide' | null;
  // Timezone-aware fields
  start_time?: string;
  end_time?: string;
  guide_timezone?: string;
  seeker_timezone?: string;
}

interface UnitWithSession {
  unitId: string;
  unitIndex: number;
  componentName: string;
  componentType: string;
  status: string;
  scheduledSession?: ScheduledSession;
}

type ActiveEngagementCardProps = {
  offer?: PackageOffer | null;
  offers?: PackageOffer[];
  guide?: Guide | null;
  onReschedule?: (session: ScheduledSession) => void;
};

const formatSessionTime = (
  session: ScheduledSession,
  seekerTimezone: string | null
) => {
  const tz = seekerTimezone || getBrowserTimezone();

  // Use UTC start_time with timezone-aware formatting when available
  if (session.start_time && tz) {
    const timeStr = formatTimeInTimezone(session.start_time, tz);
    const tzAbbrev = getTimezoneAbbreviation(session.start_time, tz);

    // Calculate end time
    let endTimeFormatted = '';
    try {
      if (session.end_time) {
        endTimeFormatted = formatTimeInTimezone(session.end_time, tz);
      } else {
        const endDate = new Date(new Date(session.start_time).getTime() + session.duration_minutes * 60000);
        endTimeFormatted = formatTimeInTimezone(endDate.toISOString(), tz);
      }
    } catch {
      // ignore
    }

    const timePart = endTimeFormatted
      ? `${timeStr} – ${endTimeFormatted}${tzAbbrev ? ` ${tzAbbrev}` : ''}`
      : `${timeStr}${tzAbbrev ? ` ${tzAbbrev}` : ''}`;

    if (isToday(session.start_time, tz)) {
      return `Today ${timePart}`;
    }
    if (isTomorrow(session.start_time, tz)) {
      return `Tomorrow ${timePart}`;
    }
    const dateStr = formatShortDateInTimezone(session.start_time, tz);
    return `${dateStr}, ${timePart}`;
  }

  // Fallback for legacy sessions
  const { formattedDate, formattedTime } = formatSessionTimeInSeekerTimezone(
    session.start_time,
    seekerTimezone,
    session.session_date,
    session.session_time
  );

  try {
    let endTimeFormatted = '';
    const startDate = new Date(`${session.session_date}T${session.session_time}Z`);
    const endDate = new Date(startDate.getTime() + session.duration_minutes * 60000);
    endTimeFormatted = endDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const dateObj = new Date(session.session_date + 'T00:00:00');
    const dateStr = dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });

    return `${dateStr} at ${formattedTime} - ${endTimeFormatted}`;
  } catch {
    return `${formattedDate} at ${formattedTime}`;
  }
};

const getLocationIcon = (locationType?: string | null) => {
  if (locationType === 'link') {
    return (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    );
  }
  if (!locationType || locationType === 'online') {
    return (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    );
  }
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
};

const OfferEngagementSection: React.FC<{
  offer: PackageOffer;
  guide?: Guide | null;
  onReschedule?: (session: ScheduledSession) => void;
}> = ({ offer, guide, onReschedule }) => {
  const navigate = useNavigate();
  const [unitsWithSessions, setUnitsWithSessions] = useState<UnitWithSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLocationSessionId, setExpandedLocationSessionId] = useState<string | null>(null);
  const [allUnitsFulfilled, setAllUnitsFulfilled] = useState(false);
  const [existingReview, setExistingReview] = useState<any>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [seekerTimezone, setSeekerTimezone] = useState<string | null>(null);
  const [confirmingSessionId, setConfirmingSessionId] = useState<string | null>(null);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    title: '',
    comment: '',
    would_recommend: true,
    is_anonymous: false,
  });

  // Load seeker timezone on mount
  useEffect(() => {
    const loadTimezone = async () => {
      const tz = await seekerDataService.getSeekerTimezone();
      setSeekerTimezone(tz || getBrowserTimezone());
    };
    loadTimezone();
  }, []);

  useEffect(() => {
    loadUnitsWithSessions();
  }, [offer.id]);

  const loadUnitsWithSessions = async () => {
    try {
      setLoading(true);
      const supabase = getSupabaseClient();

      // Check if a review already exists for this offer
      const { data: reviewData, error: reviewError } = await supabase
        .from('guide_reviews')
        .select('*')
        .eq('offer_id', offer.id)
        .maybeSingle();

      if (!reviewError && reviewData) {
        setExistingReview(reviewData);
      }

      // Get all units for this offer
      const sortedUnits = [...(offer.units ?? [])].sort((a, b) => a.unitIndex - b.unitIndex);

      // Get scheduled session IDs from units (units link TO sessions via scheduledSessionId)
      const scheduledSessionIds = sortedUnits
        .map(u => u.scheduledSessionId)
        .filter((id): id is string => id != null);

      let sessions: ScheduledSession[] | null = [];
      if (scheduledSessionIds.length > 0) {
        const { data: sessionsData, error: sessionsError } = await supabase
          .from('scheduled_sessions')
          .select('id, guide_id, session_type, session_date, session_time, session_status, duration_minutes, location, location_details, session_link, scheduled_by, start_time, end_time, guide_timezone, seeker_timezone')
          .in('id', scheduledSessionIds)
          .in('session_status', ['scheduled', 'confirmed']);

        if (sessionsError) {
          console.error('Failed to fetch scheduled sessions:', sessionsError);
        }

        sessions = sessionsData as ScheduledSession[] | null;
      }

      // Map units with their scheduled sessions
      const unitsData: UnitWithSession[] = sortedUnits.map(unit => {
        const session = sessions?.find(s => s.id === unit.scheduledSessionId);
        return {
          unitId: unit.id.toString(),
          unitIndex: unit.unitIndex,
          componentName: unit.componentName,
          componentType: unit.componentType,
          status: unit.status,
          scheduledSession: session || undefined
        };
      });

      // Define therapeutic journey order
      const getSessionTypeOrder = (componentType: string): number => {
        const type = componentType.toLowerCase();
        if (type === 'preparation') return 1;
        if (type === 'journey' || type === 'ceremony') return 2;
        if (type === 'integration') return 3;
        if (type === 'consultation') return 4;
        if (type === 'follow_up') return 5;
        return 6; // Other types
      };

      // Sort: scheduled sessions first (by date), then unscheduled (by therapeutic journey order)
      const sortedUnitsData = unitsData.sort((a, b) => {
        const aHasSession = !!a.scheduledSession;
        const bHasSession = !!b.scheduledSession;

        // If one has a session and the other doesn't, scheduled comes first
        if (aHasSession && !bHasSession) return -1;
        if (!aHasSession && bHasSession) return 1;

        // If both have sessions, sort by date/time
        if (aHasSession && bHasSession) {
          const aDateTime = a.scheduledSession!.start_time
            ? new Date(a.scheduledSession!.start_time)
            : new Date(`${a.scheduledSession!.session_date}T${a.scheduledSession!.session_time}Z`);
          const bDateTime = b.scheduledSession!.start_time
            ? new Date(b.scheduledSession!.start_time)
            : new Date(`${b.scheduledSession!.session_date}T${b.scheduledSession!.session_time}Z`);
          return aDateTime.getTime() - bDateTime.getTime();
        }

        // If neither has a session, sort by therapeutic journey order
        const aOrder = getSessionTypeOrder(a.componentType);
        const bOrder = getSessionTypeOrder(b.componentType);
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        // If same type, sort by unit index
        return a.unitIndex - b.unitIndex;
      });

      // Filter out fulfilled units - they should not appear in active engagement
      const activeUnitsData = sortedUnitsData.filter(unit => unit.status !== 'fulfilled');

      // Check if all units are fulfilled (no active units remaining)
      const allFulfilled = sortedUnits.length > 0 && activeUnitsData.length === 0;
      setAllUnitsFulfilled(allFulfilled);

      setUnitsWithSessions(activeUnitsData);
      setExpandedLocationSessionId((prev) =>
        activeUnitsData.some((unit) => unit.scheduledSession?.id === prev) ? prev : null,
      );
    } catch (err) {
      console.error('Error loading units with sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSession = (sessionId: string) => {
    navigate('/home/waiting-room', { state: { sessionId } });
  };

  const handleReschedule = (session: ScheduledSession) => {
    if (onReschedule) {
      onReschedule(session);
    }
  };

  const handleConfirmSession = async (sessionId: string) => {
    if (confirmingSessionId) return;
    setConfirmingSessionId(sessionId);
    try {
      await seekerDataService.confirmSession(sessionId);
      loadUnitsWithSessions(); // Refresh data to show updated status
    } catch (err) {
      console.error('Failed to confirm session:', err);
      alert('Failed to confirm session. Please try again.');
    } finally {
      setConfirmingSessionId(null);
    }
  };

  const handleSubmitReview = async () => {
    if (reviewData.rating === 0) {
      alert('Please select a rating');
      return;
    }

    try {
      setSubmittingReview(true);
      const supabase = getSupabaseClient();

      const { data, error } = await supabase.functions.invoke('add-review', {
        body: {
          offer_id: offer.id,
          rating: reviewData.rating,
          title: reviewData.title || null,
          comment: reviewData.comment || null,
          would_recommend: reviewData.would_recommend,
          is_anonymous: reviewData.is_anonymous,
          visibility: 'internal',
        },
      });

      if (error) {
        throw error;
      }

      setExistingReview(data.data);
      setShowReviewModal(false);
      setReviewData({
        rating: 0,
        title: '',
        comment: '',
        would_recommend: true,
        is_anonymous: false,
      });
    } catch (error: any) {
      console.error('Error submitting review:', error);
      alert(error.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#2C2731]" />
        </div>
      </section>
    );
  }

  // If all units are fulfilled and a review exists, hide this package completely
  if (allUnitsFulfilled && existingReview) {
    return null;
  }

  // If all units are fulfilled, show review section instead
  if (allUnitsFulfilled) {
    return (
      <section className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
        <div className="flex flex-col">
          <div className="flex-shrink-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/60">
              Completed Engagement
            </p>
            <h3 className="mt-2 text-lg font-semibold text-[#2C2731]">{offer.name}</h3>
            <p className="text-sm text-[#2C2731]/70 ph-mask">
              With {guide?.name || 'your guide'}
            </p>
          </div>

          {existingReview ? (
            <div className="mt-4">
              <div className="rounded-lg border border-[#2C2731]/60 bg-white/60 p-4">
                <h4 className="font-semibold text-[#2C2731]">Your Review</h4>
                <div className="mt-2 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg
                      key={star}
                      className={`h-5 w-5 ${star <= existingReview.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                {existingReview.title && (
                  <p className="mt-2 font-medium text-[#2C2731]">{existingReview.title}</p>
                )}
                {existingReview.comment && (
                  <p className="mt-2 text-sm text-[#2C2731]/80">{existingReview.comment}</p>
                )}
                <p className="mt-3 text-xs text-[#2C2731]/60">
                  Submitted {new Date(existingReview.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <div className="rounded-lg border border-[#2C2731]/60 bg-white/60 p-4">
                <h4 className="font-semibold text-[#2C2731]">Package Complete!</h4>
                <p className="mt-2 text-sm text-[#2C2731]/80 ph-mask">
                  You've completed all sessions. We'd love to hear about your experience with {guide?.name || 'your guide'}.
                </p>
                <button
                  onClick={() => setShowReviewModal(true)}
                  className="mt-4 w-full rounded-lg bg-[#2C2731] py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#2C2731]"
                >
                  Write a Review
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Review Modal */}
        {showReviewModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-xl">
              <h3 className="text-xl font-semibold text-[#2C2731]">Review Your Experience</h3>
              <p className="mt-1 text-sm text-[#2C2731]/70 ph-mask">
                With {guide?.name || 'your guide'}
              </p>

              <div className="mt-4 space-y-4">
                {/* Star Rating */}
                <div>
                  <label className="block text-sm font-medium text-[#2C2731]">
                    Rating <span className="text-red-600">*</span>
                  </label>
                  <div className="mt-1 flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewData({ ...reviewData, rating: star })}
                        className="transition hover:scale-110"
                      >
                        <svg
                          className={`h-8 w-8 ${star <= reviewData.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="review-title" className="block text-sm font-medium text-[#2C2731]">
                    Title (optional)
                  </label>
                  <input
                    type="text"
                    id="review-title"
                    value={reviewData.title}
                    onChange={(e) => setReviewData({ ...reviewData, title: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-[#2C2731]/40 bg-white px-3 py-2 text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/20"
                    placeholder="Sum up your experience"
                  />
                </div>

                {/* Comment */}
                <div>
                  <label htmlFor="review-comment" className="block text-sm font-medium text-[#2C2731]">
                    Your Review (optional)
                  </label>
                  <textarea
                    id="review-comment"
                    value={reviewData.comment}
                    onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                    rows={4}
                    className="mt-1 w-full rounded-lg border border-[#2C2731]/40 bg-white px-3 py-2 text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/20"
                    placeholder="Share your thoughts..."
                  />
                </div>

                {/* Would Recommend */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="would-recommend"
                    checked={reviewData.would_recommend}
                    onChange={(e) => setReviewData({ ...reviewData, would_recommend: e.target.checked })}
                    className="h-4 w-4 rounded border-[#2C2731]/40 text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]/20"
                  />
                  <label htmlFor="would-recommend" className="text-sm text-[#2C2731]">
                    I would recommend this guide
                  </label>
                </div>

                {/* Is Anonymous */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is-anonymous"
                    checked={reviewData.is_anonymous}
                    onChange={(e) => setReviewData({ ...reviewData, is_anonymous: e.target.checked })}
                    className="h-4 w-4 rounded border-[#2C2731]/40 text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]/20"
                  />
                  <label htmlFor="is-anonymous" className="text-sm text-[#2C2731]">
                    Submit anonymously
                  </label>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowReviewModal(false)}
                  disabled={submittingReview}
                  className="flex-1 rounded-lg border border-[#2C2731] bg-[#FFFCEF] py-2 text-sm font-semibold text-[#2C2731] transition hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReview}
                  disabled={submittingReview || reviewData.rating === 0}
                  className="flex-1 rounded-lg bg-[#2C2731] py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 disabled:opacity-50"
                >
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
      <div className="flex flex-col">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/60">
            Your Active Engagement
          </p>
          <h3 className="mt-2 text-lg font-semibold text-[#2C2731]">{offer.name}</h3>
          <p className="text-sm text-[#2C2731]/70 ph-mask">
            With {guide?.name || 'your guide'}
          </p>
        </div>

        <div className="mt-4 space-y-3">
          {unitsWithSessions.map((unit, index) => {
            const session = unit.scheduledSession;
            const isScheduled = !!session;
            const locationKind = session?.location ?? 'online';
            const isInPerson = locationKind === 'in_person';
            const isLink = locationKind === 'link';
            const showLocationDetails = expandedLocationSessionId === session?.id;

            return (
              <div key={unit.unitId} className="rounded-lg border border-[#2C2731]/60 bg-[#FFFCEF] p-4">
                <h4 className="font-semibold text-[#2C2731]">{unit.componentName}</h4>
                <p className="mt-1 text-sm text-[#2C2731]/70">
                  {unit.componentType.charAt(0).toUpperCase() + unit.componentType.slice(1)} session
                </p>

                {isScheduled && session ? (
                  <>
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-[#2C2731]/80">
                      <div className="flex items-center gap-1">
                        <svg className="h-4 w-4 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatSessionTime(session, seekerTimezone)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[#2C2731]">
                        {getLocationIcon(locationKind)}
                        <span>{isLink ? 'External Link' : isInPerson ? 'In person' : 'Online'}</span>
                      </div>
                    </div>

                    {session.session_status === 'scheduled' && session.scheduled_by === 'guide' && (
                      <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <div className="flex items-center space-x-2">
                          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-blue-800">Confirm This Session</p>
                            <p className="text-xs text-blue-700">Your guide has scheduled this session. Please confirm to proceed.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-3 flex flex-col gap-2">
                      {session.session_status === 'scheduled' && session.scheduled_by === 'guide' ? (
                        <button
                          onClick={() => handleConfirmSession(session.id)}
                          disabled={confirmingSessionId === session.id}
                          className="w-full rounded-lg bg-[#304031] py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {confirmingSessionId === session.id ? 'Confirming...' : 'Confirm Session'}
                        </button>
                      ) : isLink && session.session_link ? (
                        <button
                          onClick={() => window.open(session.session_link!, '_blank', 'noopener,noreferrer')}
                          disabled={session.session_status === 'scheduled'}
                          className="w-full rounded-lg bg-[#304031] py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#304031] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {session.session_status === 'scheduled' ? 'Pending Confirmation' : 'Open External Link'}
                        </button>
                      ) : isInPerson ? (
                        <button
                          onClick={() =>
                            setExpandedLocationSessionId((prev) => (prev === session.id ? null : session.id))
                          }
                          className="w-full rounded-lg border border-[#304031] bg-[#FFFCEF] py-2 text-sm font-semibold text-[#304031] transition hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                        >
                          View Details
                        </button>
                      ) : (
                        <button
                          onClick={() => handleJoinSession(session.id)}
                          disabled={session.session_status === 'scheduled'}
                          className="w-full rounded-lg bg-[#304031] py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#304031] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {session.session_status === 'scheduled' ? 'Pending Confirmation' : 'Join'}
                        </button>
                      )}
                      <button
                        onClick={() => handleReschedule(session)}
                        className="w-full rounded-lg border border-[#304031] bg-[#FFFCEF] py-2 text-sm font-semibold text-[#304031] transition hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                      >
                        Reschedule
                      </button>
                      <AddToCalendarButton
                        sessionId={session.id}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#304031] bg-[#FFFCEF] px-3 py-2 text-sm font-semibold text-[#304031] transition-colors hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2"
                      />
                    </div>

                    {isInPerson && showLocationDetails && (
                      <div className="mt-3 rounded-lg border border-dashed border-[#2C2731]/40 bg-white/80 px-3 py-2 text-sm text-[#2C2731]">
                        {session.location_details?.trim() ? session.location_details : 'Your guide will share location details soon.'}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mt-3 rounded-lg border border-dashed border-[#2C2731]/40 bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731]/70">
                    Not yet scheduled
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const ActiveEngagementCard: React.FC<ActiveEngagementCardProps> = ({ offer, offers, guide, onReschedule }) => {
  const offersToRender = offers && offers.length > 0
    ? offers
    : offer
      ? [offer]
      : [];

  if (offersToRender.length === 0) {
    return null;
  }

  if (offersToRender.length === 1) {
    return (
      <OfferEngagementSection
        offer={offersToRender[0]}
        guide={guide}
        onReschedule={onReschedule}
      />
    );
  }

  return (
    <div className="space-y-4">
      {offersToRender.map((offerItem) => (
        <OfferEngagementSection
          key={offerItem.id}
          offer={offerItem}
          guide={guide}
          onReschedule={onReschedule}
        />
      ))}
    </div>
  );
};

export default ActiveEngagementCard;
